
import React, { useMemo } from "react";
import { MonitoringEvent } from "@/types/monitoring";
import { TestResponse, Question } from "@/types/test-response";
import { format } from "date-fns";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AlertTriangle, TabletSmartphone, Clock } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Card } from "@/components/ui/card";

interface HorizontalTestTimelineProps {
  testResponse: TestResponse;
}

export function HorizontalTestTimeline({ testResponse }: HorizontalTestTimelineProps) {
  // Extract data from testResponse
  const events = testResponse.events || [];
  const questions = testResponse.test?.questions?.sort((a, b) => a.order - b.order) || [];
  const startTime = new Date(testResponse.started_at).getTime();
  const endTime = testResponse.completed_at 
    ? new Date(testResponse.completed_at).getTime() 
    : Date.now();
  
  // Calculate total duration (minimum 1000ms to avoid division by zero)
  const totalDuration = Math.max(endTime - startTime, 1000);
  
  // Sort events chronologically and filter out webcam capture events
  const sortedEvents = useMemo(() => {
    return [...events]
      .filter(event => event.event_type !== 'webcam_capture')
      .sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
  }, [events]);

  // Get icon for event type
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'tab_switch':
        return <TabletSmartphone className="h-3 w-3" />;
      case 'timing_anomaly':
        return <Clock className="h-3 w-3" />;
      default:
        return <AlertTriangle className="h-3 w-3" />;
    }
  };
  
  // Get color for event severity
  const getEventColor = (severity: number) => {
    if (severity > 2) return "bg-red-500 dark:bg-red-400";
    if (severity > 1) return "bg-amber-500 dark:bg-amber-400";
    return "bg-green-500 dark:bg-green-400";
  };
  
  // Format event timestamp with hour:minute:second
  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'HH:mm:ss');
  };
  
  // Format event details
  const formatEventDetails = (event: MonitoringEvent) => {
    const eventData = event.event_data || {};
    
    switch (event.event_type) {
      case 'tab_switch':
        return `Tab switch detected${eventData.duration ? ` (${eventData.duration}s)` : ''}`;
      case 'timing_anomaly':
        return `Time anomaly detected${eventData.duration ? ` (${eventData.duration}s)` : ''}`;
      default:
        return `Monitoring event: ${event.event_type}`;
    }
  };
  
  // For empty state
  if (events.length === 0) {
    return (
      <Card className="p-4 text-center text-muted-foreground">
        No monitoring events recorded during this test.
      </Card>
    );
  }
  
  return (
    <Card className="p-6 relative">
      <div className="mb-8">
        <h3 className="text-base font-semibold mb-2">Test Timeline</h3>
        <p className="text-sm text-muted-foreground">
          Timeline shows events during the test from {format(startTime, 'HH:mm:ss')} to {format(endTime, 'HH:mm:ss')}
        </p>
      </div>
      
      <div className="relative mt-6 pt-8 pb-12">
        {/* Main timeline bar */}
        <div className="h-1.5 bg-muted rounded-full w-full absolute top-8"></div>
        
        {/* Question section markers */}
        <div className="absolute top-0 left-0 w-full">
          {questions.map((question, index) => {
            // Approximate question sections - without exact timestamps
            // This is a visual approximation dividing the timeline equally
            const sectionWidth = 100 / questions.length;
            const leftPosition = index * sectionWidth;
            
            return (
              <div 
                key={question.id}
                className="absolute transform -translate-x-1/2"
                style={{ left: `${leftPosition}%` }}
              >
                <div className="h-3 w-3 rounded-full bg-secondary mb-1 mx-auto"></div>
                <div className="text-xs font-medium">Q{index + 1}</div>
              </div>
            );
          })}
        </div>
        
        {/* Start time */}
        <div className="absolute -bottom-4 left-0 text-xs text-muted-foreground">
          {format(startTime, 'HH:mm:ss')}
        </div>
        
        {/* End time */}
        <div className="absolute -bottom-4 right-0 text-xs text-muted-foreground">
          {format(endTime, 'HH:mm:ss')}
        </div>
        
        {/* Event markers */}
        <TooltipProvider delayDuration={200}>
          {sortedEvents.map((event, index) => {
            const eventTime = new Date(event.created_at).getTime();
            const positionPercentage = ((eventTime - startTime) / totalDuration) * 100;
            const clampedPosition = Math.min(Math.max(positionPercentage, 0), 100);
            
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <div 
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                    style={{ 
                      left: `${clampedPosition}%`,
                      top: '8px'  // Center on timeline bar
                    }}
                  >
                    <div className={cn(
                      "h-4 w-4 rounded-full flex items-center justify-center",
                      getEventColor(event.severity)
                    )}>
                      {getEventIcon(event.event_type)}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{event.event_type.replace('_', ' ')}</span>
                      <span className="text-xs">{formatTimestamp(event.created_at)}</span>
                    </div>
                    <p className="text-sm">{formatEventDetails(event)}</p>
                    
                    {event.question_id && (
                      <div className="text-xs text-muted-foreground pt-1 border-t border-border/20">
                        Question: {event.event_data?.question_number || "Unknown"}
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
        
        {/* Hover interaction for the timeline */}
        <HoverCard openDelay={200}>
          <HoverCardTrigger asChild>
            <div className="absolute inset-0 cursor-help"></div>
          </HoverCardTrigger>
          <HoverCardContent>
            <div className="text-sm">
              <p className="font-medium">Test Timeline</p>
              <p className="text-muted-foreground text-xs mt-1">
                This timeline shows all monitoring events detected during the test. 
                Hover over colored dots to see event details.
              </p>
              <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border/20">
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="text-xs">Low severity</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                  <span className="text-xs">Medium severity</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-3 rounded-full bg-red-500"></div>
                  <span className="text-xs">High severity</span>
                </div>
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>
    </Card>
  );
}
